import { supabase } from '../utils/supabase';
import { 
  StockMovement, 
  StockMovementForm, 
  MovementFilters,
  MovementSummary,
  ApiResponse 
} from '../types';

export class MovementService {
  
  // Get all movements with filters and pagination
  static async getMovements(
    filters: MovementFilters = {},
    page = 1,
    limit = 20
  ): Promise<ApiResponse<{ movements: MovementSummary[], total: number }>> {
    try {
      const offset = (page - 1) * limit;
      
      const { data, error } = await supabase.rpc('get_movements_with_summary', {
        p_movement_type: filters.movement_type || null,
        p_store_id: filters.store_id || null,
        p_date_from: filters.date_from || null,
        p_date_to: filters.date_to || null,
        p_reference_search: filters.reference_number || null,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        return { data: { movements: [], total: 0 }, error: error.message };
      }

      if (data?.success) {
        return {
          data: {
            movements: data.data.movements || [],
            total: data.data.total || 0
          },
          error: null
        };
      } else {
        return {
          data: { movements: [], total: 0 },
          error: data?.error || 'Failed to fetch movements'
        };
      }
    } catch (error: any) {
      return {
        data: { movements: [], total: 0 },
        error: error.message || 'Failed to fetch movements'
      };
    }
  }

  // Get movement by ID with details
  static async getMovementById(id: string): Promise<ApiResponse<StockMovement | null>> {
    try {
      const { data, error } = await supabase.rpc('get_movement_by_id', {
        p_movement_id: id
      });

      if (error) {
        return { data: null, error: error.message };
      }

      if (data?.success) {
        return { data: data.data, error: null };
      } else {
        return {
          data: null,
          error: data?.error || 'Movement not found'
        };
      }
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch movement'
      };
    }
  }

  // Create new movement with details
  static async createMovement(movementData: StockMovementForm, userId?: string): Promise<ApiResponse<StockMovement | null>> {
    try {
      // Check if user ID is provided
      if (!userId) {
        return { data: null, error: 'User ID is required' };
      }

      // Reference number is auto-generated in the new RPC function
      // const referenceNumber = await this.generateReferenceNumber(movementData.movement_type);

      // Prepare items array for the database function
      const itemsJson = movementData.details.map(detail => ({
        item_id: detail.item_id,
        quantity: detail.quantity,
        unit_price: detail.unit_price || 0,
        unit_cost: detail.unit_cost || 0,
        order_references: detail.order_references
      }));

      // Generate reference number
      const referenceNumber = await this.generateReferenceNumber(movementData.movement_type);
      
      console.log('=== RPC MOVEMENT CREATION ===');
      console.log('Movement Type:', movementData.movement_type);
      console.log('Items JSON:', itemsJson);
      console.log('Items JSON String:', JSON.stringify(itemsJson));
      
      // Use create_stock_movement for multiple items support
      const { data: result, error } = await supabase.rpc('create_stock_movement', {
        p_user_id: userId,
        p_movement_type: movementData.movement_type,
        p_reference_number: referenceNumber,
        p_store_id: movementData.store_id,
        p_items: itemsJson,
        p_movement_date: movementData.movement_date,
        p_remarks: movementData.remarks || '',
        p_deliveryman_id: movementData.deliveryman_id || null,
        p_delivery_hash: movementData.delivery_hash || null
      });

      console.log('Raw RPC Result type:', typeof result);
      console.log('Raw RPC Result:', result);
      console.log('RPC Error:', error);

      if (error) {
        console.error('RPC Error Details:', error);
        return { data: null, error: error.message };
      }

      // The create_stock_movement function returns (success, movement_id, message)
      let parsedResult;
      
      if (typeof result === 'string') {
        console.log('Response is string - parsing PostgreSQL record format');
        const match = result.match(/\(([^,]+),([^,]+),"([^"]+)"\)/);
        if (match) {
          parsedResult = {
            success: match[1] === 't',
            movement_id: match[2],
            message: match[3]
          };
        }
      } else if (result && typeof result === 'object' && result.success !== undefined) {
        console.log('Response is object - using directly');
        parsedResult = result;
      } else if (Array.isArray(result) && result[0]) {
        console.log('Response is array - using first element');
        parsedResult = result[0];
      } else {
        console.log('Unknown response format - treating as error');
        return { data: null, error: 'Unknown response format from database' };
      }

      console.log('Final Parsed Result:', parsedResult);

      if (!parsedResult?.success) {
        console.error('RPC Failed:', parsedResult);
        return { data: null, error: parsedResult?.message || 'Failed to create movement' };
      }

      console.log('✅ RPC Success! Movement ID:', parsedResult.movement_id);
      
      // Fetch the created movement with all details
      const createdMovement = await this.getMovementById(parsedResult.movement_id);
      return createdMovement;
    } catch (error: any) {
      console.error('=== FINAL ERROR ===', error);
      return {
        data: null,
        error: error.message || 'Failed to create movement'
      };
    }
  }

  // Update movement
  static async updateMovement(id: string, movementData: Partial<StockMovementForm>): Promise<ApiResponse<StockMovement | null>> {
    try {
      const { data: updateResult, error: movementError } = await supabase.rpc('update_stock_movement', {
        p_movement_id: id,
        p_movement_type: movementData.movement_type,
        p_store_id: movementData.store_id,
        p_movement_date: movementData.movement_date,
        p_remarks: movementData.remarks,
        p_deliveryman_id: movementData.deliveryman_id
      });

      if (movementError || !updateResult?.success) {
        return { data: null, error: updateResult?.error || movementError?.message || 'Failed to update movement' };
      }

      // If details are provided, update them
      if (movementData.details) {
        // Delete existing details using RPC
        const { data: deleteResult, error: deleteError } = await supabase.rpc('delete_movement_details', {
          p_movement_id: id
        });
        
        if (deleteError || !deleteResult?.success) {
          return { data: null, error: deleteResult?.error || deleteError?.message || 'Failed to delete existing details' };
        }

        // Insert new details
        const detailsToInsert = movementData.details.map(detail => ({
          movement_id: id,
          item_id: detail.item_id,
          quantity: detail.quantity,
          unit_price: detail.unit_price,
          unit_cost: detail.unit_cost,
          total_amount: detail.quantity * detail.unit_price,
          order_references: detail.order_references
        }));

        const { data: insertResult, error: detailsError } = await supabase.rpc('insert_movement_details', {
          p_movement_id: id,
          p_details: JSON.stringify(detailsToInsert)
        });

        if (detailsError || !insertResult?.success) {
          return { data: null, error: insertResult?.error || detailsError?.message || 'Failed to insert movement details' };
        }

        // Note: Stock levels are automatically updated by the create_stock_movement RPC function
      }

      return await this.getMovementById(id);
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to update movement'
      };
    }
  }

  // Delete movement
  static async deleteMovement(id: string): Promise<ApiResponse<boolean>> {
    try {
      // Delete movement and its details using RPC
      const { data: deleteResult, error: deleteError } = await supabase.rpc('delete_stock_movement', {
        p_movement_id: id
      });
      
      if (deleteError || !deleteResult?.success) {
        return { data: false, error: deleteResult?.error || deleteError?.message || 'Failed to delete movement' };
      }

      return { data: true, error: null };
    } catch (error: any) {
      return {
        data: false,
        error: error.message || 'Failed to delete movement'
      };
    }
  }

  // Generate reference number with timestamp to avoid collisions
  static async generateReferenceNumber(movementType: string): Promise<string> {
    const prefix = {
      'purchase_in': 'PIN',
      'adjustment': 'ADJ',
      'delivery_out': 'OUT',
      'delivery_return': 'RET',
      'opening_stock': 'OPN'
    }[movementType] || 'MOV';

    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    
    // Use timestamp-based sequence to avoid collisions
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    
    return `${prefix}-${year}${month}${day}-${timestamp}`;
  }

  // Update stock levels based on movement
  private static async updateStockLevels(
    movementId: string,
    storeId: string,
    movementType: string,
    details: any[]
  ): Promise<void> {
    const isStockIn = ['purchase_in', 'delivery_return', 'opening_stock'].includes(movementType);

    for (const detail of details) {
      let quantityChange = detail.quantity;
      
      // Determine if this is a stock increase or decrease
      if (movementType === 'adjustment') {
        // For adjustments, positive quantity = stock increase, negative = decrease
        // quantityChange already has the correct sign
      } else if (isStockIn) {
        quantityChange = Math.abs(quantityChange); // Ensure positive for stock in
      } else {
        quantityChange = -Math.abs(quantityChange); // Ensure negative for stock out
      }

      // Update store stock using RPC
      const { data: stockResult, error: stockError } = await supabase.rpc('update_store_stock', {
        p_store_id: storeId,
        p_item_id: detail.item_id,
        p_quantity_change: quantityChange,
        p_new_average_cost: detail.unit_cost || 0
      });

      if (stockError || !stockResult?.success) {
        console.error('Failed to update stock for item:', detail.item_id, stockError);
        // Continue processing other items instead of failing completely
      }
    }
  }

  // Get movement types
  static getMovementTypes() {
    return [
      { value: 'purchase_in', label: 'Purchase In', icon: '📦', color: 'green' },
      { value: 'adjustment', label: 'Stock Adjustment', icon: '⚖️', color: 'blue' },
      { value: 'delivery_out', label: 'Delivery Out', icon: '🚚', color: 'orange' },
      { value: 'delivery_return', label: 'Delivery Return', icon: '↩️', color: 'purple' },
      { value: 'opening_stock', label: 'Opening Stock', icon: '🏗️', color: 'gray' }
    ];
  }

  // Get movement statistics
  static async getMovementStats(storeId?: string): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase.rpc('get_movement_statistics', {
        p_store_id: storeId || null,
        p_date_from: null,
        p_date_to: null
      });

      if (error) {
        return { data: null, error: error.message };
      }

      if (data?.success) {
        return { data: data.data, error: null };
      } else {
        return {
          data: null,
          error: data?.error || 'Failed to fetch movement statistics'
        };
      }
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch movement statistics'
      };
    }
  }

  // Get item transaction history with running balance (bank style)
  static async getItemTransactionHistory(
    itemId: string, 
    storeId: string, 
    limit = 100, 
    offset = 0
  ): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase.rpc('get_item_transaction_history', {
        p_item_id: itemId,
        p_store_id: storeId,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        return { data: null, error: error.message };
      }

      if (data?.success) {
        return { data: data.data, error: null };
      } else {
        return {
          data: null,
          error: data?.error || 'Failed to fetch item transaction history'
        };
      }
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch item transaction history'
      };
    }
  }

  // Get store transaction history (all items) with bank-style display
  static async getStoreTransactionHistory(
    storeId: string,
    filters: {
      itemSearch?: string;
      movementType?: string;
      dateFrom?: string;
      dateTo?: string;
    } = {},
    limit = 50,
    offset = 0
  ): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase.rpc('get_store_transaction_history', {
        p_store_id: storeId,
        p_item_search: filters.itemSearch || null,
        p_movement_type: filters.movementType || null,
        p_date_from: filters.dateFrom || null,
        p_date_to: filters.dateTo || null,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        return { data: null, error: error.message };
      }

      if (data?.success) {
        return { data: data.data, error: null };
      } else {
        return {
          data: null,
          error: data?.error || 'Failed to fetch store transaction history'
        };
      }
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch store transaction history'
      };
    }
  }

  // Duplicate/reorder a movement
  static async duplicateMovement(
    movementId: string, 
    newMovementType?: string, 
    newStoreId?: string, 
    newRemarks?: string
  ): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase.rpc('duplicate_movement', {
        p_movement_id: movementId,
        p_new_movement_type: newMovementType || null,
        p_new_store_id: newStoreId || null,
        p_new_remarks: newRemarks || null
      });

      if (error || !data?.success) {
        return { data: null, error: data?.error || error?.message || 'Failed to duplicate movement' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to duplicate movement'
      };
    }
  }

  // Get movement templates for reordering
  static async getMovementTemplates(
    storeId?: string, 
    movementType?: string, 
    limit = 10
  ): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase.rpc('get_movement_templates', {
        p_store_id: storeId || null,
        p_movement_type: movementType || null,
        p_limit: limit
      });

      if (error || !data?.success) {
        return { data: [], error: data?.error || error?.message || 'Failed to get movement templates' };
      }

      return { data: data.data || [], error: null };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get movement templates'
      };
    }
  }

  // Create movement from template
  static async createMovementFromTemplate(
    templateMovementId: string,
    storeId: string,
    movementType: string,
    remarks?: string
  ): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase.rpc('create_movement_from_template', {
        p_template_movement_id: templateMovementId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_remarks: remarks || null
      });

      if (error || !data?.success) {
        return { data: null, error: data?.error || error?.message || 'Failed to create movement from template' };
      }

      return { data: data.data, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to create movement from template'
      };
    }
  }

  // Get recent movements for Dashboard
  static async getRecentMovements(
    storeId: string,
    movementType?: string,
    limit = 5
  ): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase.rpc('get_recent_movements', {
        p_store_id: storeId,
        p_movement_type: movementType || null,
        p_limit: limit
      });

      if (error || !data?.success) {
        return { data: [], error: data?.error || error?.message || 'Failed to get recent movements' };
      }

      return { data: data.data || [], error: null };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get recent movements'
      };
    }
  }

  // Get dashboard sessions
  static async getDashboardSessions(
    storeId: string,
    movementType?: string,
    limit = 10
  ): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_sessions', {
        p_store_id: storeId,
        p_movement_type: movementType || null,
        p_limit: limit
      });

      if (error || !data?.success) {
        return { data: [], error: data?.error || error?.message || 'Failed to get dashboard sessions' };
      }

      return { data: data.data || [], error: null };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get dashboard sessions'
      };
    }
  }

}